// state/nav-override.tsx — V28 round-2 NAVIGATION REWORK (nav-actions registry).
//
// A screen-scoped DYNAMIC registry for the global swipe / edge-tap navigation.
// Originally this only carried a dynamic "next" ROUTE string (the Appendice
// detail's async next-sibling). The rework generalizes it into a nav-ACTIONS
// registry so a screen can register imperative `onBack` / `onNext` CALLBACKS —
// needed for Simula (move between questions) and the Fast lesson (advance to the
// next micro-topic) where "back"/"next" are NOT routes but in-screen actions.
//
// Back-compat is preserved verbatim: the legacy `nextOverride` route string +
// `setNextOverride` setter still exist and still feed `useScreenFlow().next`, so
// PrincipleDetailScreen keeps working unchanged.
//
// Scope: ephemeral, not persisted. A screen registers on mount/update and MUST
// clear on unmount so an override never leaks onto another screen. `null` means
// "no dynamic override — use the static FLOW value".

import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  type ReactNode,
} from 'react';

/** Imperative nav actions a screen can register. Either may be omitted. */
export interface NavActions {
  /** Called for swipe-right / left-edge tap (back). Overrides the route-based
   *  goBack when present. */
  onBack?: () => void;
  /** Called for swipe-left / right-edge tap (next). Overrides the route-based
   *  goNext (and the legacy nextOverride route) when present. */
  onNext?: () => void;
}

interface NavOverrideContextValue {
  /** Legacy: when non-null, the resolved route a screen wants "next" to go to,
   *  overriding the static FLOW.next. Still used by PrincipleDetailScreen. */
  nextOverride: string | null;
  /** Legacy: register (or clear, with `null`) the dynamic next ROUTE. */
  setNextOverride: (route: string | null) => void;
  /** Registered imperative nav actions (onBack / onNext). Empty object = none. */
  navActions: NavActions;
  /** Register (replacing) the imperative nav actions. Pass `{}` (or call the
   *  cleanup it implies) to clear. */
  setNavActions: (actions: NavActions) => void;
}

const NO_OP_ACTIONS: NavActions = {};

const NavOverrideContext = createContext<NavOverrideContextValue | null>(null);

export function NavOverrideProvider({ children }: { children: ReactNode }) {
  const [nextOverride, setNextOverride] = useState<string | null>(null);
  const [navActions, setNavActionsState] = useState<NavActions>(NO_OP_ACTIONS);

  // Stable setter — screens register via an effect and clear on unmount.
  const setNavActions = useCallback((actions: NavActions) => {
    setNavActionsState(actions ?? NO_OP_ACTIONS);
  }, []);

  const value = useMemo<NavOverrideContextValue>(
    () => ({ nextOverride, setNextOverride, navActions, setNavActions }),
    [nextOverride, navActions, setNavActions],
  );

  return <NavOverrideContext.Provider value={value}>{children}</NavOverrideContext.Provider>;
}

/**
 * Read/write the dynamic nav registry. Returns a safe no-op shape when called
 * outside a provider so screens rendered in isolation (e.g. unit tests without
 * AppFrame) don't throw — they simply have no dynamic-nav wiring.
 */
export function useNavOverride(): NavOverrideContextValue {
  const ctx = useContext(NavOverrideContext);
  if (!ctx) {
    return {
      nextOverride: null,
      setNextOverride: () => {},
      navActions: NO_OP_ACTIONS,
      setNavActions: () => {},
    };
  }
  return ctx;
}
